import type {
  CreateSocialAccountInput,
  SocialAccountDto,
  SocialMetricDto,
  UpdateSocialAccountInput,
  UpsertSocialMetricInput,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, query } from '../../lib/api.js';

export interface SocialFilter {
  company?: string;
  platform?: string;
}

/** The monitoring view — active accounts with their latest numbers and flag. */
export function useSocialOverview(filter: SocialFilter) {
  return useQuery({
    queryKey: ['social', 'overview', filter],
    queryFn: () => api.get<SocialAccountDto[]>(`/social/overview${query({ ...filter })}`),
  });
}

/** The register — every account (active and inactive) for the given filter. */
export function useSocialAccounts(filter: SocialFilter) {
  return useQuery({
    queryKey: ['social', 'accounts', filter],
    queryFn: () => api.get<SocialAccountDto[]>(`/social/accounts${query({ ...filter })}`),
  });
}

export function useAccountMetrics(accountId: string | null) {
  return useQuery({
    queryKey: ['social', 'metrics', accountId],
    queryFn: () => api.get<SocialMetricDto[]>(`/social/accounts/${accountId}/metrics`),
    enabled: !!accountId,
  });
}

/** Invalidate every SoMe query after a change so both views refresh. */
function useInvalidateSocial() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ['social'] });
}

export function useCreateAccount() {
  const invalidate = useInvalidateSocial();
  return useMutation({
    mutationFn: (body: CreateSocialAccountInput) =>
      api.post<SocialAccountDto>('/social/accounts', body),
    onSuccess: invalidate,
  });
}

export function useUpdateAccount() {
  const invalidate = useInvalidateSocial();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateSocialAccountInput }) =>
      api.patch<SocialAccountDto>(`/social/accounts/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteAccount() {
  const invalidate = useInvalidateSocial();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/social/accounts/${id}`),
    onSuccess: invalidate,
  });
}

export function useUpsertMetric(accountId: string) {
  const invalidate = useInvalidateSocial();
  return useMutation({
    mutationFn: (body: UpsertSocialMetricInput) =>
      api.put<SocialMetricDto>(`/social/accounts/${accountId}/metrics`, body),
    onSuccess: invalidate,
  });
}
